import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Choice,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import {
  ANNOUNCEMENT_AUDIENCE_RULES,
  ANNOUNCEMENT_AUDIENCE_RULE_INFO,
  type AnnouncementAudienceRule,
  type AnnouncementAudienceRuleKey,
  type AnnouncementKycStatus,
} from '@dayon/contracts';

import { api, ApiRequestError } from '../../lib/api.ts';
import { AudienceSummary, type AudienceCount } from './AudienceSummary.tsx';

/**
 * Compose an announcement, and see how many people it is for.
 *
 * ── WHAT CHANGED, AND WHY THE OLD NOTICE IS GONE ──────────────────────────
 *
 * This dialog carried a notice saying that nothing in the platform delivers an
 * announcement and that the missing piece is an AUDIENCE RESOLVER rather than a
 * mailer. That was true and it is no longer: `audience_rule` is a closed
 * vocabulary now, `services/api/src/modules/platform-admin/audience.ts`
 * resolves it, and `POST /api/platform/announcements/:id/send` fans it out
 * through the same outbox the invites go through. The notice has been replaced
 * rather than softened, and what replaced it is the pair of things that are
 * still true: email is the only channel with a transport, and a promotional
 * message carries no one-click unsubscribe because no route in the player
 * portal turns marketing off.
 *
 * ── THE AUDIENCE IS A CLOSED SET, AND THAT IS THE POINT ───────────────────
 *
 * It used to be free text with five suggestions, deliberately: a closed set
 * would have implied the options meant something to the platform, and they did
 * not. They do now — each of the four is a predicate that can be counted before
 * anybody presses send — so free text would be the dishonest choice, because a
 * sentence nobody can evaluate cannot be delivered to.
 *
 * THE COUNT IS FETCHED WHILE YOU COMPOSE. Not as a convenience: a send is
 * irreversible in the only way that matters, and an operator who learns the size
 * afterwards has turned one mistake into two thousand.
 */

/** `platform.announcement.channels`, and the enum in @dayon/contracts. */
const CHANNELS = [
  { code: 'IN_APP', label: 'In-app' },
  { code: 'EMAIL', label: 'Email' },
  { code: 'PUSH', label: 'Push' },
  { code: 'SMS', label: 'SMS' },
] as const;

const KYC_STATUSES: readonly AnnouncementKycStatus[] = [
  'UNVERIFIED',
  'PENDING',
  'VERIFIED',
  'REJECTED',
];

interface Composed {
  id: string;
  ref: string;
  status: string;
  missingLocales: string[];
  audience: AudienceCount;
}

export function ComposeAnnouncementDialog({
  open,
  onClose,
  onDone,
  brands,
  defaultBrandId,
  namedPlayerIds,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  brands: { id: string; name: string }[];
  defaultBrandId: string | null;
  /**
   * A list handed over from the players roster, or undefined for an ordinary
   * compose. It is never gathered here — see the note on `composableRules`.
   */
  namedPlayerIds?: readonly string[] | undefined;
}) {
  /* A handed-over list is the audience, so the dropdown is not shown at all
     rather than shown and ignored. Empty is treated as absent: arriving with
     `players=` and nothing after it is a malformed link, not an audience of
     nobody. */
  const named = namedPlayerIds && namedPlayerIds.length > 0 ? namedPlayerIds : null;
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [kind, setKind] = useState<'MANDATORY' | 'PROMOTIONAL'>('PROMOTIONAL');
  const [english, setEnglish] = useState('');
  const [filipino, setFilipino] = useState('');
  const [rule, setRule] = useState<AnnouncementAudienceRuleKey>('ALL_PLAYERS');
  const [kycStatus, setKycStatus] = useState<AnnouncementKycStatus>('VERIFIED');
  const [channels, setChannels] = useState<string[]>(['EMAIL']);
  /* Chosen lazily: `brands` arrives from GET /api/brands a moment after this
     dialog mounts, and freezing brands[0] into the initial state gave the
     affiliate form an empty select it then refused to submit. */
  const [chosenBrand, setChosenBrand] = useState<string | null>(null);
  const brandId = chosenBrand ?? defaultBrandId ?? '';
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [composed, setComposed] = useState<Composed | null>(null);

  /* ── The live count ────────────────────────────────────────────────────────
     Three states, and the third is not the second. `null` while it has not been
     asked for, a number when it has been answered, and `countFailed` when the
     request did not come back — which must never render as a zero, because a
     confident zero produced by a failed request is exactly the figure somebody
     would send against. */
  const [audience, setAudience] = useState<AudienceCount | null>(null);
  const [counting, setCounting] = useState(false);
  const [countFailed, setCountFailed] = useState(false);

  /**
   * A PROMOTION MAY NOT BE ADDRESSED TO SELF-EXCLUDED PLAYERS.
   *
   * The option is disabled with its true reason rather than hidden, because an
   * operator who cannot find it will assume the platform does not know about
   * self-exclusion at all — and the whole point is that it does. The API refuses
   * the same combination, so this cannot be worked around by a stale form.
   */
  const ruleAllowed = (key: AnnouncementAudienceRuleKey): boolean =>
    effectiveKind !== 'PROMOTIONAL' || ANNOUNCEMENT_AUDIENCE_RULE_INFO[key].promotionalAllowed;

  /**
   * NAMED_PLAYERS IS NOT COMPOSED HERE, and the type system is what said so.
   *
   * Every other rule is a property, so naming the key is the whole of it. A
   * named list needs the list, which this dialog has no way to gather — and
   * building a player picker inside the composer would be a second, worse copy
   * of the roster's own selection.
   *
   * It is therefore raised FROM the players roster, which already has the
   * checkboxes, the brand scope and the search. Leaving it out of this dropdown
   * is not hiding a capability: an operator who wants to write to particular
   * people starts where those people are listed.
   */
  const composableRules = ANNOUNCEMENT_AUDIENCE_RULES.filter((key) => key !== 'NAMED_PLAYERS');

  const audienceRule: AnnouncementAudienceRule = named
    ? { rule: 'NAMED_PLAYERS', playerIds: [...named] }
    : rule === 'KYC_STATUS'
      ? { rule: 'KYC_STATUS', kycStatus }
      : { rule: rule as 'ALL_PLAYERS' };

  const close = () => {
    setKind('PROMOTIONAL');
    setEnglish('');
    setFilipino('');
    setRule('ALL_PLAYERS');
    setKycStatus('VERIFIED');
    setChannels(['EMAIL']);
    setChosenBrand(null);
    setFields({});
    setComposed(null);
    setAudience(null);
    setCountFailed(false);
    onClose();
  };

  /* Switching to Promotional while the self-exclusion audience is selected does
     not leave an impossible pair on screen: the rule moves back to the widest
     one and the notice below says what happened. Leaving it selected-but-
     refused would mean the form's own state disagrees with its own validation. */
  useEffect(() => {
    if (!ruleAllowed(rule)) setRule('ALL_PLAYERS');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  /**
   * WITH A HANDED-OVER LIST THE KIND IS DETERMINED, NOT CHOSEN — and deriving
   * it is the fix for a bug this replaced.
   *
   * It used to be an effect: open with a list, and `setKind('MANDATORY')` ran
   * on mount. But an effect cannot beat the render it lives in. `kind` still
   * held its PROMOTIONAL default while the counting effect fired in the SAME
   * commit, so the form's first act was to ask the API to count a promotional
   * notice addressed to a hand-picked list — which the API refuses, correctly
   * and by design. The operator opened the composer and was met with an error
   * dialog about self-exclusion they had done nothing to provoke.
   *
   * Derived, there is no moment when the form holds a combination the server
   * would refuse. `kind` remains the operator's choice for every ordinary
   * compose; it is simply not consulted when the answer is already settled.
   */
  const effectiveKind: 'MANDATORY' | 'PROMOTIONAL' = named ? 'MANDATORY' : kind;

  const countAudience = useCallback(async () => {
    setCounting(true);
    /* REBUILT HERE rather than closed over, so `rule`, `kycStatus` and the
       handed-over list are the dependencies of this callback — a memoised
       counter holding a stale rule object would answer the previous question
       with the current label. Pulling this into a helper broke exactly that,
       and the lint rule said so.

       THE NAMED LIST HAS TO BE FIRST, and leaving it out is not a cosmetic
       miss: `rule` still holds its default while a list is in hand, so the
       counter asked for ALL_PLAYERS and answered with the whole roster —
       thousands — beside a form that would send to two. An operator reading
       that number before pressing send is the entire reason the count is here. */
    const asked: AnnouncementAudienceRule = named
      ? { rule: 'NAMED_PLAYERS', playerIds: [...named] }
      : rule === 'KYC_STATUS'
        ? { rule: 'KYC_STATUS', kycStatus }
        : { rule: rule as 'ALL_PLAYERS' };
    try {
      const result = await api.post<{ audience: AudienceCount; maxAudience: number }>(
        '/api/platform/announcements/audience/preview',
        { kind: effectiveKind, audience: asked, ...(brandId ? { brandId } : {}) },
      );
      setAudience(result.audience);
      setCountFailed(false);
    } catch (error: unknown) {
      /* The number is cleared, not kept. A stale count beside a changed rule is
         the one thing worse than no count at all. */
      setAudience(null);
      setCountFailed(true);
      showError({
        title: 'The audience could not be counted',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was counted, and nothing was created.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setCounting(false);
    }
  }, [effectiveKind, rule, kycStatus, brandId, named, showError]);

  /* Re-counted whenever the answer would change. Every input here is a select,
     so this fires on discrete choices rather than on keystrokes. */
  useEffect(() => {
    if (!open || composed !== null) return;
    void countAudience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, composed, effectiveKind, rule, kycStatus, brandId, named]);

  const toggleChannel = (code: string, on: boolean) =>
    setChannels((current) =>
      on ? [...new Set([...current, code])] : current.filter((c) => c !== code),
    );

  const submit = async () => {
    const problems: Record<string, string> = {};
    if (english.trim() === '' && filipino.trim() === '') {
      problems['copy'] = 'Write the copy in at least one locale — both are needed to send it.';
    }
    if (channels.length === 0) {
      problems['channels'] = 'Pick at least one channel. A send with none could reach nobody.';
    }
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<Composed>('/api/platform/announcements', {
        kind: effectiveKind,
        copy: {
          ...(english.trim() ? { 'en-PH': english.trim() } : {}),
          ...(filipino.trim() ? { 'fil-PH': filipino.trim() } : {}),
        },
        channels,
        audience: audienceRule,
        ...(brandId ? { brandId } : {}),
      });
      setComposed(result);
      setFields({});
      toast(`${result.ref} saved as a draft.`);
      onDone();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not save the draft',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was created. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      wide
      title={composed === null ? 'Compose announcement' : `${composed.ref} saved as a draft`}
      actions={
        composed === null ? (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="primary" busy={busy} onClick={() => void submit()}>
              Save as draft
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        )
      }
    >
      {composed === null ? (
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="broadcast" />
            <div>
              This composes a <strong>draft</strong> and tells nobody. Sending is a separate,
              permissioned step from the table — it needs{' '}
              <span className="tabular">access.manage</span> — and it asks you to confirm the
              recipient count before it writes to anybody.
            </div>
          </div>

          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              <strong>Email is the only channel that delivers.</strong> There is no player
              notification store behind <em>in-app</em>, no device registration behind{' '}
              <em>push</em> and no gateway behind <em>SMS</em>, so an announcement without email
              can be composed and cannot be sent.
              <br />
              <br />
              <strong>A promotional message reaches only players who agreed to one.</strong>{' '}
              Marketing consent is recorded per player and withdrawable by them, and a
              promotional send also requires a <em>confirmed</em> address — so the count below
              may be far smaller than the brand's roster, and today it is usually nought,
              because nobody has been asked yet. That is the honest number rather than a
              missing filter. There is still no one-click unsubscribe in the email; it points
              the player at Email preferences in their own account, which does turn it
              off.
            </div>
          </div>

          <Field
            label="Kind"
            hint="Mandatory notices ignore marketing opt-out; promotional ones never do. The same audience reaches different people depending on this one word — a self-excluded player is withheld from a promotion and still receives a notice."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={effectiveKind}
                onChange={(e) =>
                  setKind(e.target.value === 'MANDATORY' ? 'MANDATORY' : 'PROMOTIONAL')
                }
              >
                {/*
                  * A NAMED LIST MAY NOT CARRY A PROMOTION, and the option says so
                  * rather than vanishing. The API refuses the pair outright —
                  * picking people by hand is how a self-excluded player is
                  * reached by a promotion despite the opt-out being recorded, so
                  * the rule is not a formality this form could talk around.
                  */}
                <option value="PROMOTIONAL" disabled={named !== null}>
                  Promotional — respects marketing opt-out
                  {named === null
                    ? ''
                    : ' — unavailable: a list you chose by hand may only carry an operational notice'}
                </option>
                <option value="MANDATORY">Mandatory — ignores marketing opt-out</option>
              </Select>
            )}
          </Field>

          <Field label="Title shown to players · English (PH)" error={fields['copy']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={english}
                onChange={(e) => setEnglish(e.target.value)}
                placeholder="September reload 50% — opt in now"
              />
            )}
          </Field>

          <Field label="Title shown to players · Filipino">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={filipino}
                onChange={(e) => setFilipino(e.target.value)}
                placeholder="Setyembre reload 50% — mag-opt in na"
              />
            )}
          </Field>

          <Field
            label="Brand"
            hint="Leave as every brand only if your access covers every brand — a platform-wide announcement is the widest scope on this screen, not the narrowest."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={brandId}
                onChange={(e) => setChosenBrand(e.target.value)}
              >
                <option value="">Every brand</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {/*
            * A HANDED-OVER LIST IS NOT A CHOICE ON THIS SCREEN, so it is stated
            * rather than offered. Rendering the dropdown beside a fixed audience
            * would be a control that changes nothing — and the count below is
            * counted from the list, not from whatever the dropdown happened to
            * be showing.
            */}
          {named ? (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                Addressed to <strong>{formatCount(named.length)} players you chose</strong> on the
                players roster. The list cannot be edited here — close this, change the selection
                there, and press Message again. Every other audience on this screen is a rule the
                platform re-resolves when the send runs; this one is these people.
              </div>
            </div>
          ) : (
            <>
              <Field
                label="Audience"
                hint="A rule the platform can count, not a sentence it cannot. Each one is counted exactly the way the send will count it."
              >
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={rule}
                    onChange={(e) => setRule(e.target.value as AnnouncementAudienceRuleKey)}
                  >
                    {composableRules.map((key) => (
                      <option key={key} value={key} disabled={!ruleAllowed(key)}>
                        {ANNOUNCEMENT_AUDIENCE_RULE_INFO[key].label}
                        {ruleAllowed(key)
                          ? ''
                          : ' — unavailable: a promotion may never be addressed to self-excluded players'}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <div className="notice notice-quiet">
                <Icon name="info" />
                <div>{ANNOUNCEMENT_AUDIENCE_RULE_INFO[rule].hint}</div>
              </div>
            </>
          )}

          {!named && rule === 'KYC_STATUS' ? (
            <Field
              label="KYC status"
              hint="Each player’s KYC status as it stands right now — the same status the KYC queue moves."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={kycStatus}
                  onChange={(e) => setKycStatus(e.target.value as AnnouncementKycStatus)}
                >
                  {KYC_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0) + s.slice(1).toLowerCase()}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          <AudienceSummary
            audience={audience}
            counting={counting}
            failed={countFailed}
            kind={effectiveKind}
          />

          <Field
            label="Channels"
            error={fields['channels']}
            hint="At least one, and email is the only one that delivers. Sending refuses a draft without it rather than reporting a send that reached nobody."
          >
            {() => (
              <div className="stack">
                {CHANNELS.map((c) => (
                  <Choice
                    key={c.code}
                    type="checkbox"
                    checked={channels.includes(c.code)}
                    onChange={(e) => toggleChannel(c.code, e.target.checked)}
                    label={c.code === 'EMAIL' ? `${c.label} — the only channel that delivers` : c.label}
                  />
                ))}
              </div>
            )}
          </Field>
        </div>
      ) : (
        <div className="stack">
          <div className="notice">
            <Icon name="check-circle" />
            <div>
              <strong>{composed.ref}</strong> is saved as a{' '}
              <span className="tabular">{composed.status}</span>. It appears in the table behind
              this dialog.
            </div>
          </div>
          {composed.missingLocales.length > 0 ? (
            <div className="notice">
              <Icon name="translate" />
              <div>
                It has no{' '}
                {composed.missingLocales
                  .map((l) => (l === 'en-PH' ? 'English (PH)' : 'Filipino'))
                  .join(' and no ')}{' '}
                copy, so it cannot be sent yet — sending refuses it and names the locale.
                Open the row and use <strong>Rewrite copy</strong> when the translation arrives.
              </div>
            </div>
          ) : null}
          <div className="notice notice-quiet">
            <Icon name="broadcast" />
            <div>
              Saving it did not tell anybody. As it stands it resolves to{' '}
              <strong className="tabular">{formatCount(composed.audience.recipients)}</strong>{' '}
              {composed.audience.recipients === 1 ? 'recipient' : 'recipients'} — measured just
              now, counted the way a send counts. Open the row and press <strong>Send</strong>{' '}
              when you are ready; it will re-count and ask you to confirm the number before it
              writes to anybody.
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
